import ROUTES from '../../constants/routes.constant';
import segmentNormalizeConstant from '../../constants/segmentNormalize.constant';

const rootRouteBySegment: Record<number, string> = {
  [segmentNormalizeConstant.DESPACHANTE]: ROUTES.PDV.AGENT,
  [segmentNormalizeConstant.VEICULOS]: ROUTES.PDV.VEHICLE,
  [segmentNormalizeConstant.GERAL]: ROUTES.PDV.GERAL,
  [segmentNormalizeConstant.COOPNORE]: ROUTES.PDV.GERAL,
  [segmentNormalizeConstant.CEMITERIO]: ROUTES.PDV.CEMITERIO,
  [segmentNormalizeConstant.EDUCACAO]: ROUTES.PDV.EDUCACAO,
  [segmentNormalizeConstant.PATIO]: ROUTES.PDV.VEHICLE,
  [segmentNormalizeConstant.ACESSORIA]: ROUTES.PDV.ASSESSORIA,
  [segmentNormalizeConstant.DETRAN]: ROUTES.PDV.DETRAN,
  [segmentNormalizeConstant.FINANCIAMENTO]: ROUTES.PDV.FINANCING,
  [segmentNormalizeConstant.RECHARGE]: ROUTES.PDV.RECHARGE,
  [segmentNormalizeConstant.BOLETO]: ROUTES.PDV.BILLET,
  [segmentNormalizeConstant.TOTEM]: ROUTES.PDV.TOTEM,
  [segmentNormalizeConstant.CARTORIO]: ROUTES.PDV.CARTORIO,
  [segmentNormalizeConstant.MONITORING]: ROUTES.MONITORING.ROOT,
  [segmentNormalizeConstant.LINK_VAREJO]: ROUTES.PAYMENT_LINKS.ROOT,
  [segmentNormalizeConstant.GERENCIAR_BOLETOS]: ROUTES.SLIPS.ROOT,
};

const secondarySegments = [
  segmentNormalizeConstant.DETRAN,
  segmentNormalizeConstant.BOLETO,
  segmentNormalizeConstant.RECHARGE,
  segmentNormalizeConstant.TOTEM,
  segmentNormalizeConstant.MONITORING,
  segmentNormalizeConstant.LINK_VAREJO,
  segmentNormalizeConstant.GERENCIAR_BOLETOS,
];

export const normalizePdvSegments = (
  segment?: string | number | Array<string | number> | null
): number[] => {
  const values = (Array.isArray(segment) ? segment : [segment])
    .flatMap((value) => String(value ?? '').split(','))
    .map((value) => value.trim())
    .filter((value) => /^\d+$/.test(value))
    .map(Number)
    .filter((value) => Number.isSafeInteger(value) && value > 0);

  return Array.from(new Set(values));
};

export const resolvePdvRootRoute = (segments: number[]): string => {
  // Mixed CREA accounts start in Geral. Other accounts keep their first valid
  // billing segment, so this hotfix does not replace vehicle/agent forms.
  if (
    segments.includes(segmentNormalizeConstant.CREA) &&
    segments.includes(segmentNormalizeConstant.GERAL)
  ) {
    return ROUTES.PDV.GERAL;
  }

  const primarySegment = segments.find(
    (segment) =>
      rootRouteBySegment[segment] && !secondarySegments.includes(segment)
  );
  const availableSegment =
    primarySegment ?? segments.find((segment) => rootRouteBySegment[segment]);

  return availableSegment === undefined
    ? ROUTES.REPORTS.ROOT
    : rootRouteBySegment[availableSegment];
};

export const resolvePdvAccess = (
  segment?: Parameters<typeof normalizePdvSegments>[0]
) => {
  const classificationSegments = normalizePdvSegments(segment);
  const models = [36, 37, 38].filter((code) =>
    classificationSegments.includes(code)
  );
  const billingEnabled = classificationSegments.includes(4);
  const billingModelConflict = models.length > 1;
  const hasExplicitBillingModel = models.length > 0;
  const formByModel: Record<number, number> = { 36: 2, 37: 3, 38: 16 };
  const billingModel =
    billingEnabled && models.length === 1 ? formByModel[models[0]] : null;
  // Existing tokens and clients retain their routes until an explicit model is
  // configured. An orphan/conflicting model must not silently grant a form.
  // Other legacy forms (education, RC, etc.) remain independent in this stage.
  const segments = hasExplicitBillingModel
    ? classificationSegments.filter(
        (code) => ![2, 3, 4, 10, 17, 36, 37, 38].includes(code)
      )
    : classificationSegments;
  if (billingModel && !segments.includes(billingModel))
    segments.push(billingModel);
  const hasSegment = (code: number) => segments.includes(code);
  const isCrea = hasSegment(segmentNormalizeConstant.CREA);
  const canAccessPdv = segments.some((code) =>
    rootRouteBySegment[code]?.startsWith(ROUTES.PDV.ROOT + '/')
  );
  const canAccessEcReports = !isCrea || canAccessPdv;

  return {
    segments,
    classificationSegments,
    billingEnabled,
    billingModel,
    billingModelConflict,
    hasBillingConfiguration: billingModel !== null,
    hasExplicitBillingModel,
    usesCartorioBilling: billingModel === 16,
    hasSegment,
    isCrea,
    isTotem: hasSegment(segmentNormalizeConstant.TOTEM),
    canAccessPdv,
    canAccessEcReports,
    canAccessPaymentLinks: hasSegment(segmentNormalizeConstant.LINK_VAREJO),
    canCreateBoleto: hasSegment(segmentNormalizeConstant.BOLETO),
    canManageSlips: hasSegment(segmentNormalizeConstant.GERENCIAR_BOLETOS),
    canAccessMonitoring: hasSegment(segmentNormalizeConstant.MONITORING),
    canAccessFinancingReports:
      canAccessEcReports && hasSegment(segmentNormalizeConstant.FINANCIAMENTO),
    rootRoute: billingModel
      ? rootRouteBySegment[billingModel]
      : resolvePdvRootRoute(segments),
    reportsRootRoute: canAccessEcReports
      ? ROUTES.REPORTS.EC
      : ROUTES.REPORTS.CREA,
  };
};
